import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogContent, DialogActions,
  Button, Typography, useTheme, Box, CircularProgress,
  TextField, InputAdornment, IconButton,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { useThemeContext } from '../../Context/ThemeContext';
import GoogleReCAPTCHA from 'react-google-recaptcha';
import { 
  verifyPasswordForReauth, 
  verifyGoogleReauth, 
  isGoogleUser
} from '../../services/reauthService';

const DeleteConnectionDialog = ({ open, connection, onClose, onConfirm }) => {
  const theme = useTheme();
  const { darkMode } = useThemeContext();

  // steps: 'confirm' → 'reauth' → 'verify'
  const [step, setStep] = useState('confirm');
  const [submitting, setSubmitting] = useState(false);

  // reCAPTCHA
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // Reauthentication state
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [reauthError, setReauthError] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);

  const borderBase = 'rgba(79, 172, 254, 0.1)';

  // Use env key if present, otherwise Google's public test key for localhost/dev
  const SITE_KEY =
    process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  console.log('SITE_KEY:', SITE_KEY);

  // Reset internal state whenever the dialog opens/closes or step changes
  useEffect(() => {
    if (open) {
      setStep('confirm');
      setCaptchaToken(null);
      setPassword('');
      setReauthError('');
      setShowPassword(false);
      setTimeout(() => recaptchaRef.current?.reset(), 0);
    } else {
      setCaptchaToken(null);
      setPassword('');
      setReauthError('');
    }
  }, [open]);

  useEffect(() => {
    // When entering verify step, make sure the widget is fresh
    if (step === 'verify') {
      setCaptchaToken(null);
      setTimeout(() => recaptchaRef.current?.reset(), 0);
    }
  }, [step]);

  const handleDeleteClick = () => {
    // Check if user is Google user or password user
    if (isGoogleUser()) {
      // For Google users, trigger popup reauthentication
      handleGoogleReauth();
    } else {
      // For password users, show password field
      setStep('reauth');
    }
  };

  const handleGoogleReauth = async () => {
    setReauthLoading(true);
    setReauthError('');
    
    try {
      const result = await verifyGoogleReauth();
      if (result.success) {
        setStep('verify');
      } else {
        setReauthError(result.error);
      }
    } catch (error) {
      setReauthError('Reauthentication failed. Please try again.');
    } finally {
      setReauthLoading(false);
    }
  };

  const handlePasswordReauth = async () => {
    if (!password.trim()) {
      setReauthError('Password is required');
      return;
    }

    setReauthLoading(true);
    setReauthError('');
    
    try {
      const result = await verifyPasswordForReauth(password);
      if (result.success) {
        setStep('verify');
      } else {
        setReauthError(result.error);
      }
    } catch (error) {
      setReauthError('Reauthentication failed. Please try again.');
    } finally {
      setReauthLoading(false);
    }
  };

  const handleFinalConfirm = async () => {
    if (!captchaToken) return; // guard
    try {
      setSubmitting(true);
      await onConfirm?.(connection?.id);
      // close on success
      onClose?.();
    } finally {
      setSubmitting(false);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  // ----- shared header icon -----
  const HeaderIcon = (
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        mb: 1,
        background: darkMode ? '#2c2c2e' : '#f7fbff',
        border: `1px solid ${borderBase}`,
        boxShadow: darkMode
          ? 'inset 0 1px 2px rgba(255,255,255,0.04)'
          : 'inset 0 1px 2px rgba(0,0,0,0.06)',
      }}
    >
      <WarningAmberOutlinedIcon sx={{ fontSize: '2rem', color: '#ff3126' }} />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 1,
          width: 520,
          overflow: 'hidden',
          backgroundColor: darkMode ? '#1e1f22' : '#ffffff',
          border: `1px solid ${borderBase}`,
          boxShadow: darkMode
            ? '0 24px 64px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.04)'
            : '0 24px 64px rgba(0,0,0,0.15), inset 0 1px 2px rgba(0,0,0,0.06)',
          backdropFilter: 'blur(4px)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: darkMode ? 'rgba(10, 14, 20, 0.55)' : 'rgba(10, 18, 30, 0.35)',
          backdropFilter: 'blur(4px)',
          '& .MuiDialog-container': { alignItems: 'center' },
        },
      }}
      transitionDuration={{ appear: 250, enter: 250, exit: 200 }}
    >
      <DialogContent
        sx={{
          py: 5,
          px: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          textAlign: 'center',
        }}
      >
        {HeaderIcon}

        {step === 'confirm' ? (
          <>
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 700,
                letterSpacing: 0.2,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              Delete Connection?
            </Typography>

            <Typography variant="body2" sx={{ fontSize: '0.95rem', fontWeight: 500, color: theme.palette.text.secondary }}>
              {connection ? (
                <>
                  You&apos;re about to delete <b>{connection.name}</b>
                  {connection.environment ? <> ({connection.environment})</> : null}. This action cannot be undone.
                </>
              ) : (
                'This action cannot be undone.'
              )}
            </Typography>

            {connection?.connector && (
              <Typography variant="body2" sx={{ mt: 0.75, fontSize: '0.85rem', color: theme.palette.text.secondary }}>
                Connector: <b>{connection.connector}</b>
              </Typography>
            )}
          </>
        ) : step === 'reauth' ? (
          <>
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 700,
                letterSpacing: 0.2,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              Reauthenticate to Continue
            </Typography>

            <Typography variant="body2" sx={{ fontSize: '0.95rem', color: theme.palette.text.secondary, mb: 2 }}>
              Please enter your password to confirm this action for <b>{connection?.name}</b>.
            </Typography>

            <Box sx={{ width: '100%', maxWidth: 300 }}>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                error={!!reauthError}
                helperText={reauthError}
                disabled={reauthLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: theme.palette.text.secondary, fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={reauthLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    backgroundColor: darkMode ? '#2c2c2e' : '#f8fafc',
                    '& fieldset': {
                      borderColor: reauthError ? '#ef4444' : borderBase,
                    },
                    '&:hover fieldset': {
                      borderColor: reauthError ? '#ef4444' : 'rgba(79, 172, 254, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: reauthError ? '#ef4444' : '#4facfe',
                    },
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 700,
                letterSpacing: 0.2,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              Verify it&apos;s you
            </Typography>

            <Typography variant="body2" sx={{ fontSize: '0.95rem', color: theme.palette.text.secondary, mb: 1 }}>
              Complete the verification to confirm deletion of <b>{connection?.name}</b>.
            </Typography>

            <Box sx={{ mt: 1 }}>
              {SITE_KEY ? (
                <>
                  <GoogleReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={SITE_KEY}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                    onErrored={() => setCaptchaToken(null)}
                    theme={darkMode ? 'dark' : 'light'}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: theme.palette.text.secondary }}>
                    This helps us prevent accidental or automated deletions.
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                  reCAPTCHA is not configured. Please set REACT_APP_RECAPTCHA_SITE_KEY environment variable.
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: '8px 24px' }}>
        <Button
          onClick={
            step === 'verify' ? () => setStep('confirm') : 
            step === 'reauth' ? () => setStep('confirm') : 
            handleClose
          }
          variant="text"
          disabled={submitting || reauthLoading}
          sx={{
            minWidth: 120,
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
          }}
        >
          {step === 'verify' || step === 'reauth' ? 'Back' : 'Cancel'}
        </Button>

        {step === 'confirm' ? (
          <Button
            onClick={handleDeleteClick}
            variant="contained"
            disabled={submitting || reauthLoading}
            sx={{
              minWidth: 140,
              height: 40,
              borderRadius: 1,
              px: 4,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 'none',
              fontSize: '0.95rem',
              backgroundImage: 'linear-gradient(90deg, #f59e0b, #f97316)',
              color: '#fff',
              '&:hover': {
                backgroundImage: 'linear-gradient(90deg, #f59e0b, #ea580c)',
                boxShadow: '0 8px 20px rgba(79, 172, 254, 0.2)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
              transition: 'all .18s ease',
            }}
          >
            Delete
          </Button>
        ) : step === 'reauth' ? (
          <Button
            onClick={handlePasswordReauth}
            variant="contained"
            disabled={reauthLoading || !password.trim()}
            sx={{
              minWidth: 160,
              height: 40,
              borderRadius: 1,
              px: 4,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 'none',
              fontSize: '0.95rem',
              backgroundImage: 'linear-gradient(90deg, #4facfe, #00f2fe)',
              color: '#fff',
              '&:hover': {
                backgroundImage: 'linear-gradient(90deg, #3b82f6, #4facfe)',
                boxShadow: '0 8px 20px rgba(79, 172, 254, 0.25)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
              transition: 'all .18s ease',
              opacity: reauthLoading || !password.trim() ? 0.85 : 1,
            }}
            startIcon={reauthLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
          >
            {reauthLoading ? 'Verifying…' : 'Verify Password'}
          </Button>
        ) : (
          <Button
            onClick={handleFinalConfirm}
            variant="contained"
            disabled={submitting || !captchaToken}
            sx={{
              minWidth: 160,
              height: 40,
              borderRadius: 1,
              px: 4,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 'none',
              fontSize: '0.95rem',
              backgroundImage: 'linear-gradient(90deg, #de1d0f, #f87f5d)',
              color: '#fff',
              '&:hover': {
                backgroundImage: 'linear-gradient(90deg, #e61405, #de1d0f)',
                boxShadow: '0 8px 20px rgba(79, 172, 254, 0.25)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
              transition: 'all .18s ease',
              opacity: submitting || !captchaToken ? 0.85 : 1,
            }}
            startIcon={submitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
          >
            {submitting ? 'Deleting…' : 'Confirm Delete'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

DeleteConnectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  connection: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    connector: PropTypes.string,
    environment: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired, // async function that performs the deletion
};

export default DeleteConnectionDialog;
